import { Col, Row } from "antd";
import cuid from "cuid";
import { get, isEmpty, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCallReceived,
  MdOutlineQrCode,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { PAYMENT_RECEIVER, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Refund = ({
  formProps,
  disabled,
  setDisabled,
  values,
  setPercent,
  errors,
  initialValues,
  setFieldValue,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const PAYMENT_RECEIVER_VALUES = {
    [PAYMENT_RECEIVER.SAME_ACCOUNT]: () =>
      set(
        initialValues,
        "payment.receiver",
        get(initialValues, "payment.payer"),
      ),
    [PAYMENT_RECEIVER.ANOTHER_ACCOUNT]: () =>
      set(initialValues, "payment.receiver", {
        id: cuid(),
        owner: { id: cuid() },
      }),
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              type="radio"
              variant="group"
              icon={<MdOutlineCallReceived size={16} />}
              label={
                SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT.LABEL
              }
              value={get(initialValues, "isPayerReceiver")}
              values={[
                {
                  value: PAYMENT_RECEIVER.SAME_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.USER_ACCOUNT,
                  popover:
                    !(
                      get(initialValues, "payment.payer.IBAN") &&
                      get(initialValues, "payment.payer.BIC")
                    ) &&
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .POPOVER,
                },
                {
                  value: PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
                  label:
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.REFUND.ACCOUNT
                      .OPTIONS.OTHER,
                },
              ]}
              errors={errors["isPayerReceiver"]}
              touched={!isEmpty(errors["isPayerReceiver"])}
              onBlur={() => setPercent(initialValues)}
              disabled={
                !(
                  get(initialValues, "payment.payer.IBAN") &&
                  get(initialValues, "payment.payer.BIC")
                )
              }
              onChange={(value) => {
                set(initialValues, "isPayerReceiver", value);
                const isSameAccount =
                  value === PAYMENT_RECEIVER.SAME_ACCOUNT;
                PAYMENT_RECEIVER_VALUES[value]();
                const newDisabled = {
                  ...disabled,
                  proof: {
                    ...disabled.proof,
                    refundAccount: isSameAccount,
                  },
                  payment: {
                    ...disabled.payment,
                    receiver: {
                      owner: {
                        firstName: isSameAccount,
                        lastName: isSameAccount,
                      },
                      IBAN: isSameAccount,
                      BIC: isSameAccount,
                    },
                  },
                };
                setDisabled(newDisabled);
                setPercent(initialValues, newDisabled);
              }}
              required={isFieldRequired("isPayerReceiver")}
            />
          </Col>
          {get(initialValues, "isPayerReceiver") ===
            PAYMENT_RECEIVER.ANOTHER_ACCOUNT && (
            <>
              <Col lg={12} xs={12}>
                <Input
                  label={SUBSCRIPTION.FORM.OWNER.LASTNAME.LABEL}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.OWNER.LASTNAME.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    "payment.receiver.owner.lastName",
                  )}
                  errors={errors["payment.receiver.owner.lastName"]}
                  touched={!isEmpty(errors["payment.receiver.owner.lastName"])}
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "payment.receiver.owner.lastName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.owner.lastName")}
                />
              </Col>
              <Col lg={12} xs={12}>
                <Input
                  label={SUBSCRIPTION.FORM.OWNER.FIRSTNAME.LABEL}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.OWNER.FIRSTNAME.PLACEHOLDER}
                  defaultValue={get(
                    initialValues,
                    "payment.receiver.owner.firstName",
                  )}
                  errors={errors["payment.receiver.owner.firstName"]}
                  touched={!isEmpty(errors["payment.receiver.owner.firstName"])}
                  onChange={({ target: { value } }) => {
                    set(
                      initialValues,
                      "payment.receiver.owner.firstName",
                      value,
                    );
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.owner.firstName")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.IBAN.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  trim={true}
                  uppercase={true}
                  type="custom"
                  placeholder={SUBSCRIPTION.FORM.IBAN.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.IBAN")}
                  errors={errors["payment.receiver.IBAN"]}
                  touched={!isEmpty(errors["payment.receiver.IBAN"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.IBAN", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.IBAN")}
                />
              </Col>
              <Col lg={24} xs={24}>
                <Input
                  label={SUBSCRIPTION.FORM.BIC.LABEL}
                  icon={<MdOutlineQrCode size={16} />}
                  placeholder={SUBSCRIPTION.FORM.BIC.PLACEHOLDER}
                  defaultValue={get(initialValues, "payment.receiver.BIC")}
                  errors={errors["payment.receiver.BIC"]}
                  touched={!isEmpty(errors["payment.receiver.BIC"])}
                  onChange={({ target: { value } }) => {
                    set(initialValues, "payment.receiver.BIC", value);
                  }}
                  onBlur={() => setPercent(initialValues)}
                  required={isFieldRequired("payment.receiver.BIC")}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Refund;
